import { deleteDeal } from '@dealstack/frontend/shared/dataExchange/services/project.service.ts';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export enum DELETE_DEAL_FORM {
	CONFIRM_DEAL_NAME = 'confirm_deal_name',
}

export const useDeleteDealController = ({ onClose }: { onClose: () => void }) => {
	const tAdminDashboard = useTranslationAdminDashboard();

	const { deal } = useContext(DealContext);

	const deleteDealRequest = useMutation({
		mutationFn: (variables: Parameters<typeof deleteDeal>[0]) => deleteDeal(variables),
	});

	const deleteDealForm = useForm({
		mode: 'all',
		resolver: zodResolver(
			z.object({
				[DELETE_DEAL_FORM.CONFIRM_DEAL_NAME]: z
					.string()
					.min(1, tAdminDashboard('v3.delete_deal.enter_company_name'))
					.refine((val) => val === deal?.name, {
						message: tAdminDashboard('v3.delete_deal.company_name_mismatch'),
					}),
			})
		),
	});

	useEffect(() => {
		if (deal?.name && deleteDealForm.getValues(DELETE_DEAL_FORM.CONFIRM_DEAL_NAME)) {
			void deleteDealForm.trigger(DELETE_DEAL_FORM.CONFIRM_DEAL_NAME);
		}
	}, [deal?.name, deleteDealForm]);

	const onSubmit = () => {
		if (!deal?.id) {
			return;
		}

		deleteDealRequest.mutate(
			{ dealId: deal.id },
			{
				onSuccess: () => {
					showToastV2({
						message: tAdminDashboard('v3.delete_deal.toast.success.message'),
					});
					onClose();
				},
			}
		);
	};

	return {
		deleteDealForm,
		deleteDealRequest,
		onSubmit,
		dealData: deal,
	};
};
